define((require) => {
    "use strict";

    var ImprovedModel = require("shared/zbone/ImprovedModel"),
        Permissions   = require("shared/security/Permissions"),
        Backbone      = require("backbone"),
        User          = require("editor/ui/user/User"),
        CompanyState  = require("shared/data/CompanyState");

    /**
     * The principal model representing the privileges assigned to the current logged in (AD) user. <p>
     *
     * A principal is obtained during login. It cannot be synced with the API in any way but via login.
     * To get the principal actually logged in, use the <tt>LoggedIn.principal()</tt> function, or listen
     * to <tt>change:principal</tt> events on <tt>LoggedIn</tt>. <p>
     *
     * The check for access rights, i.e. roles and privileges, <b>must</b> always be done on a principal, not on
     * an actual user. Regardless, the API will always ensure the correct privileges are enforced, even if the
     * frontend "forgets" it. <p>
     *
     * To test if a principal has <b>at least</b> has a given role, simply do:
     * <pre>
     *    // Get logged in principal (if not null)...
     *    var principal = LoggedIn.principal();
     *
     *    if (principal.can(Permissions.Roles.MANAGER)) {
     *        // if here, principal has all the privileges of the manager role
     *    }
     *    ..
     *    if (principal.cannot(Permissions.Roles.PUBLISHER) {
     *        // if here, principal does not have all the privileges of the publisher role
     *    }
     * </pre>
     *
     * In addition, any entity model declaring a raw or Backbone <tt>role</tt> property storing a role name or
     * an actual role, a role name, a role, or a privilege can all be tested against the principal in the same
     * way, like:
     * <pre>
     *    var entity = {role: "Manager", ..};
     *    if (principal.can(entity)) {
     *        // principal has at least all the privileges of "entity"
     *    }
     *    ..
     *    var user = new User(..); // e.g. user.get("role")
     *    if (principal.cannot(user)) {
     *        // principal does not have "user" does not have the all the privileges granted to "user"...
     *    }
     *    ..
     *    var privilege = Permissions.Privileges.Publish;
     *    if (principal.can(privilege)) {
     *        // principal has the Publish privilege...
     *    }
     *    if (principal.cannot(privilege)) {
     *        // principal does not have the Publish privilege...
     *    }
     *
     *    // Note: neat short cut for checking specific privilege of the currently logged in principal:
     *    if (Permissions.Privileges.Publish.can()) { // or can(principal)
     *        // currently logged in principal has at least the publish privilege
     *    }
     *    ..
     * </pre>
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     *
     * @class Principal
     * @constructor
     */
    var Principal = ImprovedModel.extend("Principal", {

        defaults: {
            displayName: null,
            type:        null,
            internal:    null,
            user:        null,
            updated:     null, // true on relogin and role/status changed, false on relogin not change, null on first login
            persistent:  null  // not from the server
        },

        relations: [{
            type: Backbone.One,
            key: "user",
            relatedModel: User
        }],

        computed: {
            role: {
                deps: ["role"],
                value: function(role) {
                    return Permissions.Roles.of(role);
                }
            },
            userId: { // for convenience
                deps: ["user"],
                value: function(user) {
                    return user ? user.get("id") : null;
                }
            },
            companyId: { // for convenience
                deps: ["user"],
                value: function(user) {
                    return user ? user.get("companyId") : null;
                }
            },
            state: {
                deps: ["state"],
                value: function(state) {
                    return CompanyState.of(state);
                }
            }
        },

        /**
         * Returns true if this principal can be used for actual login, and thus access to the API, false otherwise. <p>
         *
         * A principal where this value is false will never be logged in via <tt>Login</tt>, i.e. never returned
         * from <tt>LoggedIn.principal()</tt>.
         *
         * @return {Boolean} True if this principal can login, false otherwise.
         */
        canLogin: function() {
            return this.get("token") !== null;
        },

        /**
         * Returns true if this principal has at least all the privileges granted to the supplied object, and thus
         * can perform the action(s) guarded by it, false if it does not.
         *
         * @param  {Role|Privilege|String|*} object The object to test; a falsey value will cause true
         *                                          to be returned. A string means a role name.
         *
         * @return {Boolean} True if this principal has at least all the privileges granted to the (defaulted) object,
         *                   false if it does not.
         * @see    #cannot
         */
        can: function(object) {
            return Permissions.Roles.can(object, this);
        },

        /**
         * Returns true if this principal does not have all the privileges granted to the supplied object, and thus
         * cannot perform the action(s) guarded by it, false if it does.
         *
         * @param  {Role|Privilege|String|*} object The object to test; a falsey value will cause true
         *                                          to be returned. A string means a role name.
         *
         * @return {Boolean} True if this principal does not have all the privileges granted to the (defaulted) object,
         *                   false if does.
         * @see    #can
         */
        cannot: function(object) {
            return Permissions.Roles.cannot(object, this);
        },

        /**
         * Returns true if this principal is for the supplied user or user id, false otherwise.
         *
         * @param {String|User} user The user or user id to test against the user associated with this principal;
         *                           a falsey value will return false.
         * @return {Boolean} True if the specified user is the user associated with this principal, false otherwise.
         */
        forUser: function(user) {
            return user && this.get("userId") === (typeof user === "string" ? user : user.get("id"));
        },

        /**
         * Returns the string representation of this principal.
         *
         * @return {String} The string representation; never null.
         */
        toString: function() {
            var user = this.get("user");
            // A null user means a principal where the credentials were validated, but with company restrictions so
            // an actual login is not possible...
            if (!user) {
                return "Principal[]";
            }
            // Include *principal* display name if different from the *user* display name, e.g. on AD login...
            var s = user.get("displayName"),
                displayName = this.get("displayName");

            if (s.indexOf(displayName) === -1) {
                s += " (" + displayName + ")";
            }
            return "Principal[name: " + s + "; role: " + this.get("role").name + "]";
        }

    });

    return Principal;
});



// WEBPACK FOOTER //
// ./src/editor/security/Principal.js